import {
  Alert,
  AlertIcon,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { DateFormControl } from 'BrokerTsLib/src/components'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { formatDate } from 'BrokerTsLib/src/Utils'
import * as yup from 'yup'

import { useMarkContractAsCancelled } from '@/hooks/useWizard'

const schema = yup.object({
  expectedEndDate: yup.date().nullable().optional(),
  requestedEndDate: yup.date().nullable().optional(),
  endDate: yup.date().required('Zadejte datum konce pojištění.'),
})

type FormValues = yup.InferType<typeof schema>

export const MarkAsCancelledButton = ({
  insuranceId,
  expectedEndDate,
  requestedEndDate,
  callback,
}: {
  insuranceId: number
  /** Předvyplnění z entity Insurance (z volání Targetu), pokud proběhlo. */
  expectedEndDate?: Date | string | null
  requestedEndDate?: Date | string | null
  callback: () => void
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { mutateAsync: markCancelled, isPending } = useMarkContractAsCancelled()

  const initialValues = () => ({
    expectedEndDate: expectedEndDate ? new Date(expectedEndDate) : null,
    requestedEndDate: requestedEndDate ? new Date(requestedEndDate) : null,
    endDate: undefined,
  })

  const methods = useForm<FormValues>({
    defaultValues: initialValues(),
    resolver: yupResolver(schema),
  })

  const handleOpen = () => {
    // Předvyplnit z aktuálních hodnot z GetInsurance
    methods.reset(initialValues())
    onOpen()
  }

  const handleClose = () => {
    methods.reset(initialValues())
    onClose()
  }

  const onSubmit = async (values: FormValues) => {
    try {
      await markCancelled({
        insuranceId,
        expectedEndDate: values.expectedEndDate ? formatDate(values.expectedEndDate) : null,
        requestedEndDate: values.requestedEndDate ? formatDate(values.requestedEndDate) : null,
        endDate: formatDate(values.endDate)!,
      })
      callback()
      handleClose()
    } catch {
      methods.setError('root', { message: 'Označení smlouvy za stornovanou se nezdařilo.' })
    }
  }

  return (
    <>
      <Button variant="outline" colorScheme="red" onClick={handleOpen}>
        Označit za stornovanou
      </Button>

      <Modal isOpen={isOpen} onClose={handleClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Označit smlouvu za stornovanou</ModalHeader>
          <ModalCloseButton />
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
              <ModalBody>
                <Stack gap={4}>
                  <DateFormControl
                    name="expectedEndDate"
                    control={methods.control}
                    label="Předpokládaný konec pojištění"
                    w="full"
                  />

                  <DateFormControl
                    name="requestedEndDate"
                    control={methods.control}
                    label="Požadovaný konec pojištění"
                    w="full"
                  />

                  <DateFormControl
                    name="endDate"
                    control={methods.control}
                    label="Konec pojištění"
                    isRequired
                    w="full"
                  />

                  {methods.formState.errors.root && (
                    <Alert status="error">
                      <AlertIcon />
                      <Text>{methods.formState.errors.root.message}</Text>
                    </Alert>
                  )}
                </Stack>
              </ModalBody>
              <ModalFooter gap={3}>
                <Button variant="ghost" onClick={handleClose} isDisabled={isPending}>
                  Zrušit
                </Button>
                <Button type="submit" colorScheme="red" isLoading={isPending}>
                  Označit za stornovanou
                </Button>
              </ModalFooter>
            </form>
          </FormProvider>
        </ModalContent>
      </Modal>
    </>
  )
}
